<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';

export default {
  components: {
    SettingsSection,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedPayloadTabIndex: 0,
      showToken: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
    payloadTabs() {
      return [
        {
          key: 'text',
          name: this.$t('INBOX_MGMT.DOCUMENTATION.TABS.TEXT'),
        },
        {
          key: 'media',
          name: this.$t('INBOX_MGMT.DOCUMENTATION.TABS.MEDIA'),
        },
        {
          key: 'pdf',
          name: this.$t('INBOX_MGMT.DOCUMENTATION.TABS.PDF'),
        },
      ];
    },
    selectedPayloadKey() {
      return this.payloadTabs[this.selectedPayloadTabIndex]?.key;
    },
    baseUrl() {
      return window.location.origin;
    },
    inboxId() {
      return this.inbox?.id;
    },
    endpointBase() {
      if (!this.accountId || !this.inboxId) return '';
      return `${this.baseUrl}/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/evolution_messages`;
    },
    userAccessToken() {
      return this.currentUser?.access_token || '';
    },
    maskedToken() {
      const token = this.userAccessToken;
      if (!token) return '';
      const len = token.length;
      if (len <= 8) return '•'.repeat(len);
      return `${token.slice(0, 4)}${'•'.repeat(Math.max(len - 8, 4))}${token.slice(-4)}`;
    },
    headersPayload() {
      return `{
  "Content-Type": "application/json",
  "api_access_token": "${this.maskedToken || 'YOUR_TOKEN'}"
}`;
    },
    payloadByKey() {
      const inboxId = this.inboxId || 1;
      return {
        text: {
          url: `${this.endpointBase}/send_text`,
          body: `{
  "number": "5511999999999",
  "message": "Hola, este es un mensaje de prueba"
}`,
          response: `{
  "id": 12345,
  "conversation_id": 678,
  "contact_id": 90,
  "inbox_id": ${inboxId},
  "content": "Hola, este es un mensaje de prueba",
  "message_type": "outgoing",
  "status": "sent",
  "created_at": 1731950893
}`,
        },
        media: {
          url: `${this.endpointBase}/send_media`,
          body: `{
  "number": "5511999999999",
  "media": "image",          // image | video | audio
  "caption": "Texto opcional",
  "link": "https://..."      // URL pública o cadena base64
}`,
          response: `{
  "id": 12346,
  "conversation_id": 678,
  "contact_id": 90,
  "inbox_id": ${inboxId},
  "content": "Texto opcional",
  "message_type": "outgoing",
  "status": "sent",
  "created_at": 1731950894
}`,
        },
        pdf: {
          url: `${this.endpointBase}/send_pdf`,
          body: `{
  "number": "5511999999999",
  "message": "Adjunto PDF",
  "file": "JVBERi0xLjQK...",  // base64 (sin prefijo data:)
  "filename": "archivo.pdf"
}`,
          response: `{
  "id": 12347,
  "conversation_id": 678,
  "contact_id": 90,
  "inbox_id": ${inboxId},
  "content": "Adjunto PDF",
  "message_type": "outgoing",
  "status": "sent",
  "created_at": 1731950895
}`,
        },
      };
    },
    currentPayload() {
      return (
        this.payloadByKey[this.selectedPayloadKey] || this.payloadByKey.text
      );
    },
  },
  methods: {
    onPayloadTabChange(index) {
      this.selectedPayloadTabIndex = index;
    },
    toggleTokenVisibility() {
      this.showToken = !this.showToken;
    },
    async copyToClipboard(value) {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        useAlert(this.$t('INBOX_MGMT.DOCUMENTATION.MESSAGES.COPIED'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.DOCUMENTATION.MESSAGES.COPY_ERROR'));
      }
    },
  },
};
</script>

<template>
  <SettingsSection
    :title="$t('INBOX_MGMT.DOCUMENTATION.TITLE')"
    :sub-title="$t('INBOX_MGMT.DOCUMENTATION.DESCRIPTION')"
  >
    <div class="space-y-6">
      <div class="space-y-2">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DOCUMENTATION.TOKEN.TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.DOCUMENTATION.TOKEN.DESCRIPTION') }}
        </p>
        <div class="flex items-center gap-2">
          <code
            class="flex-1 px-3 py-2 text-sm font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong overflow-x-auto whitespace-nowrap"
          >
            {{
              userAccessToken
                ? showToken
                  ? userAccessToken
                  : maskedToken
                : '—'
            }}
          </code>
          <button
            type="button"
            class="px-2 py-2 text-xs font-medium rounded-md border border-n-strong text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
            :disabled="!userAccessToken"
            @click="toggleTokenVisibility"
          >
            {{
              showToken
                ? $t('INBOX_MGMT.DOCUMENTATION.TOKEN.HIDE')
                : $t('INBOX_MGMT.DOCUMENTATION.TOKEN.SHOW')
            }}
          </button>
          <button
            type="button"
            class="px-3 py-2 text-xs font-medium rounded-md bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
            :disabled="!userAccessToken"
            @click="copyToClipboard(userAccessToken)"
          >
            {{ $t('INBOX_MGMT.DOCUMENTATION.COPY') }}
          </button>
        </div>
      </div>

      <div class="space-y-2">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DOCUMENTATION.ENDPOINT.TITLE') }}
        </h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.DOCUMENTATION.ENDPOINT.BASE_URL') }}
            </p>
            <code
              class="block px-3 py-2 text-sm font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong break-all"
            >
              {{ baseUrl }}
            </code>
          </div>
          <div>
            <p class="mb-1 text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.DOCUMENTATION.ENDPOINT.INBOX_ID') }}
            </p>
            <code
              class="block px-3 py-2 text-sm font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong break-all"
            >
              {{ inboxId || '—' }}
            </code>
          </div>
        </div>
      </div>

      <div class="space-y-3">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DOCUMENTATION.PAYLOADS.TITLE') }}
        </h4>
        <woot-tabs
          class="[&_ul]:p-0"
          :index="selectedPayloadTabIndex"
          :border="false"
          @change="onPayloadTabChange"
        >
          <woot-tabs-item
            v-for="(tab, index) in payloadTabs"
            :key="tab.key"
            :index="index"
            :name="tab.name"
            :show-badge="false"
            is-compact
          />
        </woot-tabs>

        <div class="space-y-3">
          <div
            class="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-2 items-start"
          >
            <span class="text-xs font-semibold text-n-slate-11 pt-2">
              {{ $t('INBOX_MGMT.DOCUMENTATION.LABELS.URL') }}
            </span>
            <code
              class="block px-3 py-2 text-sm font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong break-all"
            >
              {{ currentPayload.url }}
            </code>
          </div>
          <div
            class="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-2 items-start"
          >
            <span class="text-xs font-semibold text-n-slate-11 pt-2">
              {{ $t('INBOX_MGMT.DOCUMENTATION.LABELS.METHOD') }}
            </span>
            <code
              class="block px-3 py-2 text-sm font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong"
            >
              {{ $t('INBOX_MGMT.DOCUMENTATION.LABELS.METHOD_VALUE') }}
            </code>
          </div>
          <div
            class="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-2 items-start"
          >
            <span class="text-xs font-semibold text-n-slate-11 pt-2">
              {{ $t('INBOX_MGMT.DOCUMENTATION.LABELS.HEADERS') }}
            </span>
            <div
              class="px-3 py-2 text-xs font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong overflow-x-auto whitespace-pre"
            >
              {{ headersPayload }}
            </div>
          </div>
          <div
            class="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-2 items-start"
          >
            <span class="text-xs font-semibold text-n-slate-11 pt-2">
              {{ $t('INBOX_MGMT.DOCUMENTATION.LABELS.BODY') }}
            </span>
            <div class="space-y-2">
              <div
                class="px-3 py-2 text-xs font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong overflow-x-auto whitespace-pre"
              >
                {{ currentPayload.body }}
              </div>
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-md bg-woot-500 text-white hover:bg-woot-600"
                @click="copyToClipboard(currentPayload.body)"
              >
                {{ $t('INBOX_MGMT.DOCUMENTATION.COPY') }}
              </button>
            </div>
          </div>
          <div
            class="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-2 items-start"
          >
            <span class="text-xs font-semibold text-n-slate-11 pt-2">
              {{ $t('INBOX_MGMT.DOCUMENTATION.LABELS.RESPONSE') }}
            </span>
            <div
              class="px-3 py-2 text-xs font-mono rounded-md bg-n-alpha-2 text-n-slate-12 border border-n-strong overflow-x-auto whitespace-pre"
            >
              {{ currentPayload.response }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsSection>
</template>
